/**
 * De frontend-sleutel voor server-side backendaanroepen (site-backend herkent
 * er de website aan en geeft haar een eigen rem, naast de rem per klant-IP).
 *
 * Bewust zonder Node-imports: `src/lib/api.js` wordt ook in clientcode
 * geïmporteerd. In de browser bestaat `FRONTEND_KLANT_IP_SECRET` niet (geen
 * NEXT_PUBLIC_), en `serverFrontendSleutelHeaders()` geeft daar sowieso niets.
 *
 * Dezelfde geldigheidsregels als `frontendSleutel()` in
 * `src/lib/luxe/ing-de-server.js`: minstens 32 zichtbare ASCII-tekens.
 * Ontbreekt de sleutel of is hij ongeldig, dan gaat er niets mee.
 */
export const FRONTEND_SLEUTEL_HEADER = "x-favotrip-frontend-sleutel";
export const FRONTEND_SLEUTEL_MIN_LENGTE = 32;
const GEHEIM_RE = /^[\x21-\x7e]+$/;

/** De geldige sleutel uit de server-env, of null (ook altijd null in de browser). */
export function serverFrontendSleutel() {
  if (typeof window !== "undefined") return null;
  const geheim = process.env.FRONTEND_KLANT_IP_SECRET;
  if (
    typeof geheim === "string" &&
    geheim.length >= FRONTEND_SLEUTEL_MIN_LENGTE &&
    GEHEIM_RE.test(geheim)
  ) {
    return geheim;
  }
  return null;
}

/** `{ "x-favotrip-frontend-sleutel": <sleutel> }` of een leeg object. */
export function serverFrontendSleutelHeaders() {
  const sleutel = serverFrontendSleutel();
  return sleutel ? { [FRONTEND_SLEUTEL_HEADER]: sleutel } : {};
}

const ABSOLUUT_RE = /^([a-z][a-z\d+\-.]*:)?\/\//i;

/**
 * True als de uiteindelijke URL (baseURL + doel, zoals axios die samenstelt;
 * een absoluut doel negeert baseURL) exact dezelfde origin heeft (schema, host,
 * poort) als `backendBase`. De sleutel gaat alleen naar onze eigen backend,
 * nooit naar een externe bestemming. Elke onduidelijkheid is false.
 */
export function isBackendUrl(doel, backendBase, baseURL = backendBase) {
  try {
    const pad = typeof doel === "string" ? doel : "";
    const volledig = ABSOLUUT_RE.test(pad)
      ? new URL(pad, backendBase)
      : new URL(`${String(baseURL || "").replace(/\/+$/, "")}/${pad.replace(/^\/+/, "")}`);
    const backend = new URL(backendBase);
    return volledig.origin !== "null" && volledig.origin === backend.origin;
  } catch {
    return false;
  }
}

/**
 * Strikte variant voor de uitkomst van `bff.getUri(config)`: parst die string
 * rechtstreeks, zonder basis, zoals de HTTP-adapter van axios (WHATWG-parser,
 * die voor- en achterliggende witruimte en C0-tekens wegstript). Een relatieve
 * uitkomst, een parsefout of een ander schema dan http(s) is false; anders moet
 * de origin exact die van `backendBase` zijn (fixronde 3 #1909).
 */
export function isBackendBestemming(uri, backendBase) {
  try {
    if (typeof uri !== "string") return false;
    const doel = new URL(uri);
    if (doel.protocol !== "http:" && doel.protocol !== "https:") return false;
    return doel.origin === new URL(backendBase).origin;
  } catch {
    return false;
  }
}

/** Als `serverFrontendSleutelHeaders()`, maar alleen voor een URL op de backend-origin. */
export function serverFrontendSleutelHeadersVoor(doel, backendBase, baseURL) {
  return isBackendUrl(doel, backendBase, baseURL) ? serverFrontendSleutelHeaders() : {};
}
